<template>
  <div class='page-content payment-methods'>
    <div class="page-title">
      <span>{{ $lang.messages.paymentMethods.toUpperCase() }}</span>
    </div>

    <div
        class="pm-content-row"
    >
      <div class="row pm-header expense-header">
        <span class="title">{{ $lang.messages.subscription }}</span>

        <button
          class="generic-btn retry-payment-btn"
          type="button"
          :disabled="!subscriptionStatus || subscriptionStatus !== 'Past due'"
          @click="showRetryPaymentModal"
        >
          {{ $lang.messages.retryPayment }}
        </button>

        <button
            class="action-btn danger-btn"
            type="button"
            :disabled="!subscriptionStatus || subscriptionStatus === 'Canceled'"
            @click="showCancelSubscriptionModal"
        >
          {{ $lang.messages.cancelSubscription }}
        </button>
  
        <!-- Cancel Subscription Modal -->
        <cancel-subscription-modal
            v-if="subscriptionStatus && subscriptionStatus !== 'Canceled'"
            ref="cancelSubscriptionComponent"
            @loadSubscriptions="loadSubscriptions"
        />
  
        <!-- Retry Payment Modal -->
        <retry-payment-modal
            v-if="subscriptionStatus && subscriptionStatus === 'Past due'"
            ref="retryPaymentComponent"
            @loadSubscriptions="loadSubscriptions"
        />

        <hr>

        <div class="pricing-payment">
          <p>
            <span class="home-pricing"><currency-sign currency="USD"></currency-sign>{{ currencyFormat(price) }}</span>
            <span class="home-pricing-month">/month</span>
            <span class="asterisk">*</span>
          </p>
          <div>
            <span class="asterisk">*</span>
            {{ $lang.messages.pricingTrialMessage1 }}
            {{ trialDuration }}
            {{ trialDuration > 1 ? trialDurationUnit + 's' : trialDurationUnit }}
            {{ $lang.messages.pricingTrialMessage2 }}
          </div>
        </div>

        <div class="action-wrapper">
          <span class="actions-ico" @click="toggleBraintreeActions()" :class="{'arrow-btn': showStep3Tooltip}">
          <span :class="{'blink' : showStep3Tooltip}"></span>
          <div>
            <img src="@/assets/images/payments/actions_ico.png" />
          </div>
        </span>

          <div class="action-menu" :class="[ braintreeActions ? 'select-menu-collapsed' : 'select-menu-not-collapsed' ]">
            <div>
              <button
                class="item btn-item"
                type="button"
                @click="showAddCardModal"
              >
                {{ $lang.messages.addBankCard }}
              </button>
            </div>
            <div>
              <button
                class="item btn-item"
                :class="{disabled: !subscriptionStatus || subscriptionStatus === 'Canceled'}"
                type="button"
                :disabled="!subscriptionStatus || subscriptionStatus === 'Canceled'"
                @click="showCancelSubscriptionModal"
              >
                {{ $lang.messages.cancelSubscription }}
              </button>
            </div>
            <div>
              <button
                class="item btn-item"
                :class="{disabled: !subscriptionStatus || subscriptionStatus !== 'Past due'}"
                type="button"
                :disabled="!subscriptionStatus || subscriptionStatus !== 'Past due'"
                @click="showRetryPaymentModal"
              >
                {{ $lang.messages.retryPayment }}
              </button>
            </div>
          </div>
        </div>

        <f-p-registration-progress-tooltip
          v-if="showStep3Tooltip"
          :class="{'enabled' : subscriptionActivated === 'current', 'payment-methods-tooltip subscription-tooltip responsive' : true, 'arrow-tooltip--before': true }"
          :stepNumber="'3'"
          style="top: calc((100% - 100px) / 2)"
          :text="$lang.messages.registration_progress_step_3">
        </f-p-registration-progress-tooltip>

        <template v-if="isShownSubscription">
          <f-p-registration-progress-tooltip
              :stepNumber="'3'"
              :text="$lang.messages.registration_progress_subscription_activated.replace(':duration', this.trialDuration).replace(':durationUnit', this.trialDuration > 0 ? this.trialDurationUnit + 's' : this.trialDurationUnit)"
              style="top: calc((100% + 186px) / 2)"
              :class="{
                'enabled' : subscriptionActivated === 'current',
                'arrow-tooltip--before': subscriptionActivated === 'finished',
                'bank-account-got-it-tooltip subscription-got-it-tooltip': true,
              }"
              @click.native="isClickedSubscription"
              v-show="isShownSubscription && isActivatedSubscriptionLocal"
          >
            <div class="fp-registration-progress-tooltip-button">
              <button @click="isClickedGotItForSubscription">
                {{ $lang.messages.registration_progress_btn_got_it_1 }}
              </button>
            </div>
          </f-p-registration-progress-tooltip>
        </template>
        
      </div>
      <hr>
      <div class="row subscriptions-row">
        <div class="col">
          <div class="title">
            {{ $lang.messages.subscriptionStatus }}
          </div>
          <button
            v-if="subscriptionStatus === 'Trial'"
            class="btn-status btn-success"
          >
            {{ $lang.messages.trialStatus }}
          </button>
          <button
            v-else-if="subscriptionStatus === 'Active'"
            class="btn-status btn-success"
          >
            {{ $lang.messages.activeStatus }}
          </button>
          <button
            v-else-if="subscriptionStatus === 'Past due'"
            class="btn-status btn-warning"
          >
            {{ $lang.messages.pastDueStatus }}
          </button>
          <button
            v-else-if="subscriptionStatus === 'Canceled'"
            class="btn-status btn-danger"
          >
            {{ $lang.messages.canceledStatus }}
          </button>
          <div class="data" v-if="subscriptionLoaded && !subscriptionStatus">
            -
          </div>
          <div class="gradient-mask" v-if="!subscriptionLoaded"></div>
        </div>
        <div class="col">
          <div class="title">
            {{ $lang.messages.lastSubscriptionPaymentDate }}
          </div>
          <div class="data" v-if="subscriptionLoaded">
            {{ lastSubscriptionPaymentDate !== null ? dateFormat(lastSubscriptionPaymentDate) : '-' }}
          </div>
          <div class="gradient-mask" v-if="!subscriptionLoaded"></div>
          <div class="title">
            {{ $lang.messages.nextSubscriptionPaymentDue }}
          </div>
          <div class="data" v-if="subscriptionLoaded">
            {{ nextSubscriptionPaymentDate !== null ? dateFormat(nextSubscriptionPaymentDate) : '-' }}
          </div>
          <div class="gradient-mask" v-if="!subscriptionLoaded"></div>
        </div>
      </div>
      <div>
        <div class="row pm-header expense-header">
          <div class="row">
            <div class="col">
              <p class="info-title">{{ $lang.messages.bonusTransactions }}</p>
              <p class="info-data" v-if="dataLoaded && bonusTransactions !== null">
                {{ bonusTransactions }}
              </p>
              <div class="gradient-mask" v-if="!dataLoaded || bonusTransactions === null"></div>
            </div>
          </div>
        </div>
      </div>
      <hr class="small-margin-bottom">

      <div class="row action-row">
        <f-p-registration-progress-tooltip
          v-if="showStep3Tooltip"
          :class="{'enabled' : subscriptionActivated === 'current', 'payment-methods-tooltip subscription-tooltip' : true, 'arrow-tooltip--before': true }"
          :stepNumber="'3'"
          style="top: calc((100% - 100px) / 2)"
          :text="$lang.messages.registration_progress_step_3">
        </f-p-registration-progress-tooltip>

        <button
          class="generic-btn"
          type="button"
          @click="showAddCardModal"
        >
          {{ $lang.messages.addCardModal }}
        </button>

      </div>

      <div class="row">
        <div class="col">
          <f-p-table-payment-methods
            :list="subscriptionCards"
            :subscriptionProp="true"
            :mockData="mockDataSubscriptions"
            :key="rerender"
            @deleteCard="deleteCard"
            @makeDefaultCard="makeDefaultCard"
          >
          </f-p-table-payment-methods>
        </div>
      </div>

      <!-- Add Card modal -->
      <add-card-modal
        ref="addCardComponent"
        :trialDuration="trialDuration + ''"
        :trialDurationUnit="trialDurationUnit"
        @loadSubscriptions="loadSubscriptions"
        @getRegistrationProgress="getRegistrationProgress">
      </add-card-modal>
    </div>

    <div
      class="pm-content-row"
      :class="{'app--locked': subscriptionActivated !== 'finished' && subscriptionActivated !== null}"
    >
      <div>
        <div class="row pm-header expense-header">
          <span class="title">{{ $lang.messages.expensePaymentMethods }}</span>
          <button
            class="generic-btn bank-account-btn"
            type="button"
            :disabled="(bankAccountAdded === 'unfinished' && bankAccountAdded === null)"
            @click="showAddBankAccountModal(false)"
            :class="{'enabled' : bankAccountAdded === 'current'}"
          >
          <span v-show="!linkTokenLoading">{{ $lang.messages.addBankAccount }}</span>
    <ClipLoader :loading="linkTokenLoading" :color="'#1554e4'" :size="'20px'"></ClipLoader>
            
          </button>

          <span class="actions-ico" @click="toggleBankAccountActions()" :class="{'arrow-btn': showStep5Tooltip}">
            <span :class="{'blink' : showStep5Tooltip}"></span>
            <div>
              <img src="@/assets/images/payments/actions_ico.png" />
            </div>
          </span>

          <div class="action-menu" :class="[ bankAccountActions ? 'select-menu-collapsed' : 'select-menu-not-collapsed' ]">
            <div>
              <button
                class="item btn-item"
                type="button"
                @click="showAddBankAccountModal(false)"
              >
                {{ $lang.messages.addBankAccount }}
              </button>
            </div>
          </div>
  
          <template v-if="!isBankAccountSkipped && !verifyingInProgress && showStep5Tooltip">
            <f-p-registration-progress-tooltip
                :class="{'enabled' : bankAccountAdded === 'current', 'back-account-tooltip' : true, 'arrow-tooltip--before': true}"
                :stepNumber="'4'"
                style="top: calc((100% - 105px) / 2)"
                :text="$lang.messages.registration_progress_step_5"
            >
              <div class="fp-registration-progress-tooltip-button">
                <button @click="skipBankAccount">
                  {{ $lang.messages.registration_progress_btn_skip }}
                </button>
              </div>
            </f-p-registration-progress-tooltip>
          </template>

          <template v-if="isShownAddedBankAccount">
            <f-p-registration-progress-tooltip
              :stepNumber="'4'"
              :text="$lang.messages.registration_progress_bank_account_added"
              style="top: calc((100% + 90px) / 2)"
              :class="{
                'enabled' : bankAccountAdded === 'current',
                'arrow-tooltip--before': bankAccountAdded === 'finished',
                'bank-account-got-it-tooltip': true,
              }"
              @click.native="isClickedAddedBankAccount"
              v-show="isShownAddedBankAccount && isAddedBankAccountLocal"
            >
              <div class="fp-registration-progress-tooltip-button">
                <button @click="isClickedGotItForBankAccount">
                  {{ $lang.messages.registration_progress_btn_got_it_2 }}
                </button>
              </div>
            </f-p-registration-progress-tooltip>
          </template>

          <!-- Add Bank Account modal -->
          <add-bank-account-modal
            ref="addBankAccountComponent"
            :class="{'enabled' : bankAccountAdded === 'current'}"
            @loadBankAccounts="loadBankAccounts"
            @handleCloseBankAccountModal="handleCloseBankAccountModal"
            @getRegistrationProgress="getRegistrationProgress"
          >
          </add-bank-account-modal>
        </div>
        <div class="row">
          <div class="col">
            <f-p-table-payment-methods
              :list="bankAccounts"
              :expenseProp="true"
              :radioNameProp="nameRadio"
              :mockData="mockDataBankAccounts"
              :bankAccountModalOpened="bankAccountModalOpened"
              :key="rerender"
              @deleteBankAccount="deleteBankAccount"
              @defaultBankAccount="defaultBankAccount"
              @finishAddingBankAccount="finishAddingBankAccount"
            >
            </f-p-table-payment-methods>
          </div>
        </div>
      </div>
    </div>
    <transactions-suspended-notice-modal  ref="transactionsSuspendedNoticeComponent" />
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapMutations } from "vuex";
  import {
    GET_BANK_ACCOUNTS,
    GET_SUBSCRIPTIONS,
    DELETE_SUBSCRIPTION_CARD,
    BRAINTREE_UPDATE,
    DELETE_CHECKBOOK_ACCOUNT,
    DEFAULT_CHECKBOOK_ACCOUNT,
    GET_REGISTRATION_PROGRESS,
    GET_PLAID_LINK_TOKEN,
    SUBSCRIPTION_PAST_DUE_LOCK
  } from "@/store/action.types";
  import { SET_CAN_SEND_FUNDS, SET_IS_SUBSCRIBED, SET_IS_BANK_ACCOUNT_SKIPPED } from "@/store/mutation.types";
  import FPTablePaymentMethods from "@/components/common/tables/FPTablePaymentMethods";
  import AddBankAccountModal from "@/components/appLayout/views/paymentMethods/AddBankAccountModal";
  import CancelSubscriptionModal from "@/components/appLayout/views/paymentMethods/CancelSubscriptionModal";
  import RetryPaymentModal from "@/components/appLayout/views/paymentMethods/RetryPaymentModal";
  import AddCardModal from "@/components/appLayout/views/paymentMethods/AddCardModal";
  import FPRegProgressTooltip from '@/components/common/FPRegistrationProgressTooltip';
  import { dateFormat } from "@/mixins/dateFormat";
  import CurrencySign from "@/components/common/CurrencySign";
  import { currencyFormat } from "@/mixins/currencyFormat";
  import TransactionsSuspendedNoticeModal from "@/components/appLayout/TransactionsSuspendedNoticeModal";
  import ClipLoader from 'vue-spinner/src/ClipLoader';
  import STATUS_CODES from "@/common/statusCodes";

  export default {
    name: "paymentMethods",
    components: {
      FPTablePaymentMethods,
      ClipLoader,
      AddBankAccountModal,
      AddCardModal,
      CancelSubscriptionModal,
      RetryPaymentModal,
      'f-p-registration-progress-tooltip': FPRegProgressTooltip,
      CurrencySign,
      TransactionsSuspendedNoticeModal
    },
    mixins: [
      dateFormat,
      currencyFormat
    ],
    data() {
      return {
        dataLoaded: false,
        mockDataBankAccounts: true,
        mockDataSubscriptions: true,
        nameRadio: 'pay-methods',
        bankAccounts: [],
        linkTokenLoading:false,
        rerender: 0,
        bankAccountActions: false,
        braintreeActions: false,
        subscriptionCards: [],
        allowReferralCode:false,
        lastSubscriptionPaymentDate: new Date(),
        nextSubscriptionPaymentDate: new Date(),
        subscriptionStatus: "",
        subscriptionLoaded: false,
        bankAccountModalOpened: false,
        isAddedBankAccountLocal: true,
        isActivatedSubscriptionLocal: true,
        price: 0,
        trialDuration: 0,
        trialDurationUnit: '',
        balance: {
          value: 0,
          currency: this.$store.getters.currency
        },
        bonusTransactions: null,
        isShownAddedBankAccountProp: false,
        isShownSubscriptionActivatedProp: false,
        verifyingInProgress: false
      };
    },
    computed: {
      ...mapGetters([
        'subscriptionActivated',
        'bankAccountAdded',
        'hasScheduledPayments',
        'hasEventOrVisitations',
        "transactionsSuspended",
        "isAppLocked",
        "isBankAccountSkipped"
      ]),
      showStep3Tooltip() {
        let isSubscriptionModalOpened = this.subscriptionActivated === 'current';
        return isSubscriptionModalOpened;
      },
      showStep5Tooltip() {
        let isBankAccountModalOpened = this.bankAccountAdded === 'current';
        return isBankAccountModalOpened;
      },
      isShownAddedBankAccount() {
        const result = this.bankAccountAdded === 'finished' && this.isShownAddedBankAccountProp;
        return result;
      },
      isShownSubscription() {
        const result = this.subscriptionActivated === 'finished' && this.isShownSubscriptionActivatedProp && (this.hasScheduledPayments !== null || this.hasEventOrVisitations !== null);
        return result;
      }
    },
    mounted() {  
      if (this.transactionsSuspended && window.localStorage.getItem("pastDue") === "1") {
        this.$refs.transactionsSuspendedNoticeComponent.show();
      }
      this.loadBankAccounts();
      this.loadSubscriptions();
      this.setLocalStorageForAddedBankAccount();
      this.setLocalStorageForActivateSubscription();
      this.isShownAddedBankAccountProp = JSON.parse(window.localStorage.getItem('isShownTooltipForAddedBankAccount'));
      this.isShownAddedBankAccountProp = JSON.parse(window.localStorage.getItem('isShownAddedBankAccountProp'));
    },
    methods: {
      ...mapActions({
        getBankAccountsAction: GET_BANK_ACCOUNTS,
        getSubscriptionsAction: GET_SUBSCRIPTIONS,
        deleteSubscriptionCard: DELETE_SUBSCRIPTION_CARD,
        braintreeUpdateAction: BRAINTREE_UPDATE,
        deleteCheckbookAccount: DELETE_CHECKBOOK_ACCOUNT,
        defaultCheckbookAccount: DEFAULT_CHECKBOOK_ACCOUNT,
        getRegistrationProgressAction: GET_REGISTRATION_PROGRESS,
        getPlaidLinkToken:GET_PLAID_LINK_TOKEN,
        setSubPastDue:SUBSCRIPTION_PAST_DUE_LOCK
      }),
      ...mapMutations({
        canSendFundsMutation: SET_CAN_SEND_FUNDS,
        isSubscribedMutation: SET_IS_SUBSCRIBED,
        setIsBankAccountSkipped: SET_IS_BANK_ACCOUNT_SKIPPED
      }),
      /**
       * @description On component mounted set the intial values for registration steps for
       * added coparent and child
       */
      setLocalStorageForAddedBankAccount() {
        if (window.localStorage.getItem('isAddedBankAccount') === 'false') {
          return
        }
        window.localStorage.setItem('isAddedBankAccount', 'true');
      },
      /**
       * @description When user clicks on button, hide the tooltip for added bank account
       */
      isClickedAddedBankAccount() {
        this.isAddedBankAccountLocal = false;
        window.localStorage.setItem('isAddedBankAccount', 'false');
      },
      /**
       * @description On component mounted set the intial values for registration steps for
       * added coparent and child
       */
      setLocalStorageForActivateSubscription() {
        if (window.localStorage.getItem('isAddedSubsciption') === 'false') {
          return
        }
        window.localStorage.setItem('isAddedSubsciption', 'true');
      },
      /**
       * @description When user clicks on button, hide the tooltip for added bank account
       */
      isClickedSubscription() {
        this.isActivatedSubscriptionLocal = false;
        window.localStorage.setItem('isAddedSubsciption', 'false');
      },
      loadBankAccounts(setGotIt) {
        this.getBankAccountsAction()
        .then(response => {
          if (response.data.data) {
            
            if(response.data.data.banks) {
              this.bankAccounts = response.data.data.banks
            }
            
            if (this.bankAccounts.length >= 1) {
              this.mockDataBankAccounts = false;
              this.canSendFundsMutation(true)
            } else {
              this.mockDataBankAccounts = true;
              this.canSendFundsMutation(false)
            }
  
            const pendingBankAccounts = this.bankAccounts.filter(account => account.status === 'PENDING');
            this.verifyingInProgress = pendingBankAccounts.length >= 1;

            this.dataLoaded = true;
            this.rerender++;
  
            this.setLocalStorageForAddedBankAccountRegistration(setGotIt);
          }
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        })
      },
      loadSubscriptions(setGotIt) {
        this.getSubscriptionsAction()
        .then(response => {
          if (response.data.data) {
            this.subscriptionCards = response.data.data.creditCardResponseDtos
            this.nextSubscriptionPaymentDate = response.data.data.nextSubscriptionPaymentDate
            this.lastSubscriptionPaymentDate = response.data.data.lastSubscriptionPaymentDate
            this.bonusTransactions = response.data.data.bonusTransactions;
            this.subscriptionStatus = response.data.data.subscriptionStatus;
            this.price = response.data.data.price;
            this.trialDuration = response.data.data.trialDuration;
            this.trialDurationUnit = response.data.data.trialDurationUnit;
            this.allowReferralCode = response.data.data.allowReferralCode;

            const isSubPastDue = localStorage.getItem('isSubscriptionPastDue');
            if(!isSubPastDue && response.data.data.accountStatus && response.data.data.accountStatus==='Subscription is past due'){
              this.setSubPastDue(true);
              this.$snotify.error("Your subscription has expired. Please add a valid payment method and retry the payment manually to continue using FamiliPay.", 'ERROR');
            }
            else if(isSubPastDue && response.data.data.accountStatus && response.data.data.accountStatus==='Active'){
               this.setSubPastDue(false);
            }

            if (this.subscriptionCards.length >= 1) {
              this.mockDataSubscriptions = false
              this.isSubscribedMutation(true)
            } else {
              this.mockDataSubscriptions = true
              this.isSubscribedMutation(false)
            }

            if (this.lastSubscriptionPaymentDate !== null || this.nextSubscriptionPaymentDate !== null) {
              this.subscriptionLoaded = true
            }

            this.setLocalStorageForSubscriptionRegistration(setGotIt);
          }
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        });
      },
      //
      // Braintree subscription
      //
      deleteCard(creditCardToken) {
        this.deleteSubscriptionCard({
          creditCardToken: creditCardToken
        })
        .then(() => {
          this.$snotify.success(this.$lang.messages.sn_deleteCardSuccess, 'SUCCESS')
          this.loadSubscriptions()
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(error.response.data.status.desc, 'ERROR')
          }
        });
      },
      makeDefaultCard(creditCardToken) {
        this.braintreeUpdateAction({
          creditCardToken: creditCardToken
        })
        .then(() => {
          this.$snotify.success(this.$lang.messages.sn_defaultCardSuccess, "SUCCESS");
          this.loadSubscriptions()
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        });
      },
      finishAddingBankAccount() {
        this.showAddBankAccountModal(true);
      },
      //
      // Checkbook payment
      //
      deleteBankAccount(bankId) {
        this.deleteCheckbookAccount({
          bankId: bankId
        })
        .then(() => {
          this.$snotify.success(this.$lang.messages.sn_deleteCheckbookAccount, "SUCCESS");
          this.loadBankAccounts();
        })
        .catch(error => {
          // If there is only one bank account present, forbid it's deletions
          if (error.response.status === 400) {
            return this.$snotify.error(this.$lang.messages.sn_checkbookSingleAccountError, 'ERROR')
          }
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        });
      },
      defaultBankAccount(bankId) {
        this.defaultCheckbookAccount(bankId)
          .then(() => {
            this.$snotify.success(this.$lang.messages.sn_defaultCheckbookAccount, "SUCCESS");
            this.loadBankAccounts()
          })
          .catch(error => {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
            }
          });
      },
      /**
       * @description When we close the modal, and we didn't call the modal action
       * close the modal and eventually show the tooltips
       * @param {String }val
       */
      handleCloseBankAccountModal() {
        this.bankAccountModalOpened = false;
      },
      showAddBankAccountModal(instantVerifying) {
        this.linkTokenLoading = true;
        this.bankAccountActions = false;
        this.bankAccountModalOpened = true;
        this.getPlaidLinkToken().then(response=>{
          this.linkTokenLoading=false;
          const token = response.data.data.linkToken;
          this.$refs.addBankAccountComponent.show(this.verifyingInProgress, instantVerifying,token);
        })
        .catch(()=>{
          this.linkTokenLoading=false;
          this.$refs.addBankAccountComponent.show(this.verifyingInProgress, instantVerifying,null,true);
        })
      },
      showAddCardModal() {
        this.braintreeActions = false;
        this.$refs.addCardComponent.show(this.subscriptionActivated,this.allowReferralCode)
      },
      togglePersonalInfoActions() {
        this.personalInfoActions = !this.personalInfoActions
      },
      toggleBankAccountActions() {
        this.bankAccountActions = !this.bankAccountActions
      },
      toggleBraintreeActions() {
        this.braintreeActions = !this.braintreeActions
      },
      /**
       * @description On component mounted set the initial values for registration steps for
       * added bank account
       */
      setLocalStorageForAddedBankAccountRegistration(setGotIt) {
        const condition = setGotIt && this.bankAccounts.length === 1 && (this.bankAccountAdded === 'finished' || this.bankAccountAdded === 'current');
        if (condition) {
          window.localStorage.setItem('isShownTooltipForAddedBankAccount', JSON.stringify(true));
          this.isShownAddedBankAccountProp = JSON.parse(window.localStorage.getItem('isShownTooltipForAddedBankAccount'));
        }
      },
      /**
       * @description When user clicks on tooltip for added bank account button, hide the tooltip
       */
      isClickedGotItForBankAccount() {
        window.localStorage.setItem('isShownTooltipForAddedBankAccount', JSON.stringify(false));
        this.isShownAddedBankAccountProp = JSON.parse(window.localStorage.getItem('isShownTooltipForAddedBankAccount'));
        this.$router.push({ name: 'Dashboard', query: { completed: 'true'} })
      },
      /**
       * @description On component mounted set the initial values for registration steps for
       * added bank account
       */
      setLocalStorageForSubscriptionRegistration(setGotIt) {
        const condition = setGotIt && this.subscriptionCards.length === 1 && (this.subscriptionActivated === 'finished' || this.subscriptionActivated === 'current');
        if (condition) {
          window.localStorage.setItem('isShownTooltipForSubscription', JSON.stringify(true));
          this.isShownSubscriptionActivatedProp = JSON.parse(window.localStorage.getItem('isShownTooltipForSubscription'));
        }
      },
      /**
       * @description When user clicks on tooltip for added bank account button, hide the tooltip
       */
      isClickedGotItForSubscription() {
        window.localStorage.setItem('isShownTooltipForSubscription', JSON.stringify(false));
        this.isShownSubscriptionActivatedProp = JSON.parse(window.localStorage.getItem('isShownTooltipForSubscription'));
      },
      getRegistrationProgress() {
        this.getRegistrationProgressAction()
        .then((response) => {
          if (response.data.data.bankAccountAdded === 'finished') {
            this.$router.push({ name: 'PaymentMethods', query: { completed: 'true'} })
          }
        })
      },
      /**
       * @description When user clicks on button open Cancel subscription modal
       */
      showCancelSubscriptionModal () {
        this.$refs.cancelSubscriptionComponent.show()
      },
      /**
       * @description When user clicks on button open Retry payment modal
       */
      showRetryPaymentModal () {
        this.$refs.retryPaymentComponent.showRetryPaymentModal()
      },
      skipBankAccount () {
        this.setIsBankAccountSkipped(true);
        this.$router.push({ name: 'Dashboard', query: { completed: 'true'} });
      }
    }
  };
</script>
